import React from "react";
import {
    makeStyles,
    createStyles,
    Dialog as MDialog,
    DialogTitle as MDialogTitle,
    Typography,
    DialogContent as MDialogContent,
    DialogActions as MDialogActions,
    Button,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import type { IResponse } from "App/Modules/Models";
import type { FormProps } from "./Form";
import { Form } from "./Form";

import type { DialogCloseReason, DialogProps } from "./Dialog";

type FormsDialogProps<TFormData extends Record<string, any>, TSubmitResponse> = DialogProps &
    Pick<FormProps<TFormData, TSubmitResponse>, "initialValues" | "validationSchema" | "onSubmitting"> & {
        onClose: (data: IResponse<TSubmitResponse>, reason: DialogCloseReason) => void;
    };

const useStyles = makeStyles(() =>
    createStyles({
        alert: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            paddingBottom: 1,
            paddingTop: 1,
        },
    }),
);

export const FormsDialog = <TFormData extends Record<string, any>, TSubmitResponse = TFormData>(
    props: FormsDialogProps<TFormData, TSubmitResponse>,
) => {
    const { t } = useTranslation();
    const { dialogOptions, initialValues, onClose, open, title, children } = props;
    const classes = useStyles();

    const handleCancel = (reason: DialogCloseReason) => {
        if (reason === "backdropClick") {
            return;
        }
        onClose(undefined, reason);
    };

    const handleEntering = (node: HTMLElement) => {
        const inputs = node?.getElementsByTagName("input");
        if (inputs?.length) {
            inputs[0].focus();
        }
    };

    return (
        <MDialog
            open={open}
            maxWidth="xs"
            disablePortal
            onClose={(_, reason) => handleCancel(reason)}
            TransitionProps={{
                onEntering: handleEntering,
            }}
            {...dialogOptions}
        >
            <Form
                initialValues={initialValues}
                validationSchema={props.validationSchema}
                onSubmitting={props.onSubmitting}
                onSubmitSuccessful={(data) => onClose(data, "formSubmit")}
                buttonContainer={MDialogActions}
                classes={{ validationErrorAlert: classes.alert }}
                extraButtons={({ isSubmitting }) => (
                    <Button onClick={() => handleCancel("cancelButtonClick")} disabled={isSubmitting}>
                        {t("Gui.Buttons.Cancel")}
                    </Button>
                )}
            >
                {() => (
                    <>
                        <MDialogTitle disableTypography>
                            <Typography variant="h2">{title}</Typography>
                        </MDialogTitle>
                        <MDialogContent dividers>{children}</MDialogContent>
                    </>
                )}
            </Form>
        </MDialog>
    );
};
